import { CardAssets, CardData, DURATION, renderToCanvas } from "./cardRenderer";

// Encodes the animated card to an H.264 MP4 (what X accepts) in the browser with WebCodecs.
// Frames are drawn and encoded as fast as the machine allows - not in real time - so a 20s clip
// takes seconds. Mediabunny is loaded on demand: it's only needed on this admin page.

export const VIDEO_SCALE = 1.5; // 1920x1080, same as the Canva exports
export const VIDEO_FPS = 30;

export const exportCardVideo = async (
    card: CardData,
    assets: CardAssets,
    onProgress?: (fraction: number) => void,
): Promise<Blob> => {
    const { Output, Mp4OutputFormat, BufferTarget, CanvasSource, QUALITY_HIGH, canEncodeVideo } = await import("mediabunny");

    const canvas = document.createElement("canvas");
    renderToCanvas(canvas, VIDEO_SCALE, 0, card, assets);

    if (!(await canEncodeVideo("avc", { width: canvas.width, height: canvas.height }))) {
        throw new Error("This browser can't encode H.264 video. Use a recent Chrome or Edge.");
    }

    const output = new Output({ format: new Mp4OutputFormat({ fastStart: "in-memory" }), target: new BufferTarget() });
    const source = new CanvasSource(canvas, { codec: "avc", bitrate: QUALITY_HIGH, keyFrameInterval: 2 });
    output.addVideoTrack(source, { frameRate: VIDEO_FPS });
    await output.start();

    const frames = Math.round(DURATION * VIDEO_FPS);
    for (let i = 0; i < frames; i++) {
        const t = i / VIDEO_FPS;
        renderToCanvas(canvas, VIDEO_SCALE, t, card, assets);
        await source.add(t, 1 / VIDEO_FPS); // awaiting respects encoder backpressure
        if (i % 15 === 0) onProgress?.(i / frames);
    }

    await output.finalize();
    onProgress?.(1);
    return new Blob([output.target.buffer!], { type: "video/mp4" });
};
